import type { Role } from '@talentbabu/types';
import { cn } from '@talentbabu/ui';
import { LogOut, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuthStore, userCan } from '../app/auth-store';
import { Logo } from '../components/Logo';
import { SecureImage } from '../components/SecureImage';
import { useLogout } from '../features/auth/api';
import { useUnreadCount } from '../features/notifications/api';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { titleCase } from '../lib/format';
import type { NavConfig } from './nav';

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Where the sidebar avatar leads: the account/settings page of the user's own panel. */
function accountPath(role: Role): string {
  switch (role) {
    case 'CANDIDATE':
      return '/candidate/settings';
    case 'EMPLOYER':
    case 'HR_MANAGER':
      return '/employer/settings';
    case 'RECRUITER':
      return '/recruiter/settings';
    default:
      return '/admin/account';
  }
}

/** Sidebars that show the unread count next to Notifications. */
const BADGE_ROUTES = new Set(['/candidate/notifications', '/employer/notifications', '/admin/notifications']);

export function DashboardLayout({ nav }: { nav: NavConfig }) {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const { data: unread = 0 } = useUnreadCount();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);
  // Long menus scroll; keep the current page's entry visible after navigation.
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }, [location.pathname]);

  if (!user) return null;
  const items = nav.items.filter((i) => !i.permission || userCan(user, i.permission));
  const mobileItems = items.filter((i) => i.mobile).slice(0, 4);
  const dark = nav.theme === 'dark';

  const signOut = () => logout.mutate(false, { onSettled: () => navigate('/login', { replace: true }) });

  const sidebar = (
    <div
      className={cn(
        'flex h-full flex-col',
        dark ? 'bg-slate-950 text-slate-300' : 'border-r border-slate-200 bg-white text-slate-600',
      )}
    >
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <Logo inverted={dark} />
        <button
          type="button"
          className="rounded-lg p-1.5 lg:hidden cursor-pointer"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <p
        className={cn(
          'shrink-0 px-5 pt-2 pb-2 text-[11px] font-semibold tracking-widest uppercase',
          dark ? 'text-slate-500' : 'text-slate-400',
        )}
      >
        {nav.title} workspace
      </p>
      <nav
        ref={navRef}
        className={cn(
          'min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-3 pb-2',
          // The admin sidebar still scrolls, but without a visible scrollbar.
          dark ? '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden' : '[scrollbar-width:thin]',
        )}
        aria-label="Main"
      >
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? dark
                    ? 'bg-brand-500/15 text-brand-300'
                    : 'bg-brand-50 text-brand-700'
                  : dark
                    ? 'hover:bg-white/5 hover:text-white'
                    : 'hover:bg-slate-100 hover:text-slate-900',
              )
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
            {label}
            {BADGE_ROUTES.has(to) && unread > 0 && (
              <span
                className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white"
                aria-label={`${unread} unread notifications`}
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className={cn('m-3 flex shrink-0 items-center gap-3 rounded-xl p-3', dark ? 'bg-white/5' : 'bg-slate-50')}>
        <Link
          to={accountPath(user.role)}
          aria-label="Account settings"
          title="Account settings"
          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-sm font-bold text-white"
        >
          {user.photoUrl ? (
            <SecureImage
              src={user.photoUrl}
              alt={user.name}
              className="h-full w-full object-cover"
              fallback={<>{initials(user.name)}</>}
            />
          ) : (
            initials(user.name)
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm font-semibold', dark ? 'text-white' : 'text-slate-900')}>{user.name}</p>
          <p className="truncate text-xs">{titleCase(user.role)}</p>
        </div>
        <button
          type="button"
          onClick={signOut}
          disabled={logout.isPending}
          className={cn('rounded-lg p-2 cursor-pointer', dark ? 'hover:bg-white/10' : 'hover:bg-slate-200')}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">{sidebar}</aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/50" onClick={() => setDrawerOpen(false)} aria-hidden />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] shadow-xl">{sidebar}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:justify-end lg:px-8">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="-ml-1 rounded-lg p-2 hover:bg-slate-100 cursor-pointer lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="lg:hidden">
            <Logo />
          </div>
          <NotificationBell />
        </header>

        <main className="mx-auto max-w-6xl px-4 py-6 pb-24 sm:px-6 lg:px-8 lg:py-8 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {mobileItems.length > 1 && (
        <nav
          className="fixed inset-x-0 bottom-0 z-20 grid border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
          style={{ gridTemplateColumns: `repeat(${mobileItems.length}, minmax(0, 1fr))` }}
          aria-label="Quick navigation"
        >
          {mobileItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium',
                  isActive ? 'text-brand-600' : 'text-slate-500',
                )
              }
            >
              <Icon className="h-5 w-5" aria-hidden />
              <span className="max-w-full truncate px-1">{label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
